/**
 * Search links (`/?search=<module>&base64Payload=<base64 JSON>`) - QA doc docs features/website/search-forms.md R-SRF8 (docs#446):
 * R-SRF8b a party outside the form's range is put back into it and the agent is warned,
 * R-SRF8c a link that cannot be read opens the empty form with a warning,
 * R-SRF8d `search` is only a module on the allow-list derived from the router configuration - never a free navigation target.
 */
import { Injector } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { Route } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

export const SEARCH_LINK_ADJUSTED_MESSAGE = 'The search parameters were adjusted to the allowed range';
export const SEARCH_LINK_UNREADABLE_MESSAGE = 'The search link could not be read - the empty search form is shown';

/** The party limits of one search form (R-MR7 for Hotels: one room 10 / 10, Auto or 2-5 rooms 30 / 20; Tours 10 / 10). */
export interface SearchLinkPartyLimits {
  minAdults: number;
  maxAdults: number;
  maxChildren: number;
}

export interface SearchLinkParty {
  adults?: number | null;
  children?: number | null;
  childrenAges?: (number | null)[] | null;
}

/**
 * The modules a search link may open: those of `supported` (the forms that read a link payload) that the router really serves as a
 * path. Anything else in `?search=` is refused, so a link can never send the agent to an arbitrary route.
 */
export function searchLinkModules(routes: readonly Route[], supported: readonly string[]): string[] {
  const paths = new Set<string>();
  const walk = (list: readonly Route[] | undefined) => {
    for (const route of list ?? []) {
      if (route.path && !route.redirectTo && !route.path.includes(':') && route.path !== '**') paths.add(route.path);
      walk(route.children);
    }
  };
  walk(routes);
  return supported.filter((module) => paths.has(module));
}

/** A whole number within [min, max]; a value that is no number becomes `fallback`. */
const clampNumber = (value: unknown, min: number, max: number, fallback: number): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
};

/**
 * Puts the party of a link payload into the form's range: adults at least `minAdults` and at most `maxAdults`, children 0 ..
 * `maxChildren`, the ages list no longer than the children. Returns the payload to apply and whether anything was changed.
 */
export function clampSearchLinkParty<T extends SearchLinkParty>(
  payload: T,
  limits: SearchLinkPartyLimits
): { payload: T; adjusted: boolean } {
  // a field the link does not carry stays out: the form keeps its own value for it
  const next = { ...payload };
  if ('adults' in payload)
    next.adults = clampNumber(payload.adults ?? NaN, limits.minAdults, limits.maxAdults, limits.minAdults);
  if ('children' in payload) next.children = clampNumber(payload.children ?? 0, 0, limits.maxChildren, 0);
  const children = next.children ?? limits.maxChildren;
  if (Array.isArray(payload.childrenAges)) next.childrenAges = payload.childrenAges.slice(0, children);
  const adjusted =
    next.adults !== payload.adults ||
    next.children !== payload.children ||
    (Array.isArray(payload.childrenAges) && payload.childrenAges.length > children);
  return { payload: next, adjusted };
}

/** A warning in the site's language at the bottom of the screen (the translations may still be loading at start-up). */
export function warnSearchLink(injector: Injector, message: string): void {
  const translate = injector.get(TranslateService, null);
  const snackBar = injector.get(MatSnackBar, null);
  if (!snackBar) return;
  const open = (text: string) =>
    snackBar.open(text, '', {
      panelClass: 'snackbar-warning',
      duration: 6000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  if (!translate) {
    open(message);
    return;
  }
  // At start-up the site loads English (the fallback) first and the agent's language after it (LocaleService.init), so a warning
  // raised by a link read in between (R-SRF8c) was shown in English on the Russian site (DEV run 29.10.2026). `stream` gives the
  // text again when the agent's language arrives; the warning is then shown again in that language - until it has closed.
  const closed = new Subject<void>();
  let shown: string | null = null;
  let current: MatSnackBarRef<TextOnlySnackBar> | undefined;
  translate
    .stream(message)
    .pipe(takeUntil(closed))
    .subscribe({
      next: (text: string) => {
        if (text === shown) return;
        shown = text;
        const ref = open(text);
        current = ref;
        // no snack bar to follow: nothing on screen can be replaced later, so the watch ends here
        if (!ref) {
          closed.next();
          return;
        }
        // opening the translated warning closes the previous one: only the warning on screen ends the watch
        ref.afterDismissed().subscribe(() => {
          if (current === ref) closed.next();
        });
      },
      // the translations could not be loaded: the warning still shows, in English (the key is the English text)
      error: (error: unknown) => {
        console.warn('Search link warning shown untranslated: the translations could not be loaded', {
          message,
          error
        });
        open(message);
      }
    });
}
